import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as PromoteIcon,
  Person as DemoteIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface User {
  id: number;
  email: string;
  role: 'admin' | 'user';
  email_verified: boolean;
  account_status: string;
  created_at: string;
  last_login?: string;
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: number | null;
    action: 'promote' | 'demote' | 'delete' | null;
    email: string;
  }>({
    open: false,
    userId: null,
    action: null,
    email: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter((user) =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await api.get('/auth/admin/users');

      if (response.data.success) {
        setUsers(response.data.data.users);
        setFilteredUsers(response.data.data.users);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || 'Erreur lors de la récupération des utilisateurs';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenConfirmDialog = (
    userId: number,
    action: 'promote' | 'demote' | 'delete',
    email: string
  ) => {
    setConfirmDialog({
      open: true,
      userId,
      action,
      email,
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      userId: null,
      action: null,
      email: '',
    });
  };

  const handleConfirmAction = async () => {
    const { userId, action } = confirmDialog;
    if (!userId || !action) return;

    try {
      let response;
      if (action === 'delete') {
        response = await api.delete(`/auth/admin/users/${userId}`);
      } else {
        const newRole = action === 'promote' ? 'admin' : 'user';
        response = await api.put(`/auth/admin/users/${userId}/role`, { role: newRole });
      }

      if (response.data.success) {
        setSuccess(
          action === 'promote'
            ? `${confirmDialog.email} a été promu administrateur`
            : action === 'demote'
            ? `${confirmDialog.email} a été rétrogradé en utilisateur`
            : `${confirmDialog.email} a été supprimé`
        );
        fetchUsers();
        handleCloseConfirmDialog();

        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || `Erreur lors de l'opération`;
      setError(errorMessage);
      handleCloseConfirmDialog();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleColor = (role: string) => {
    return role === 'admin' ? 'secondary' : 'primary';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Gestion des utilisateurs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez les rôles et permissions des utilisateurs de la plateforme
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualiser">
            <IconButton onClick={fetchUsers} disabled={isLoading} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/admin')}
          >
            Retour
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search Bar */}
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Rechercher un utilisateur par email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper elevation={3}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? 'Essayez avec un autre terme de recherche'
                : 'Aucun utilisateur enregistré dans le système'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Rôle</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Email vérifié</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Statut</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                    Date d'inscription
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                    Dernière connexion
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {user.email}
                        {user.id === currentUser?.id && (
                          <Chip
                            label="Vous"
                            size="small"
                            sx={{ ml: 1 }}
                            color="info"
                          />
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.email_verified ? 'Vérifié' : 'Non vérifié'}
                        color={user.email_verified ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          user.account_status === 'approved'
                            ? 'Approuvé'
                            : user.account_status === 'pending'
                            ? 'En attente'
                            : 'Rejeté'
                        }
                        color={getStatusColor(user.account_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(user.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.last_login ? formatDate(user.last_login) : 'Jamais'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {user.role === 'user' ? (
                          <Tooltip title="Promouvoir en administrateur">
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              startIcon={<PromoteIcon />}
                              onClick={() =>
                                handleOpenConfirmDialog(user.id, 'promote', user.email)
                              }
                              disabled={user.account_status !== 'approved'}
                            >
                              Promouvoir
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Rétrograder en utilisateur">
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={<DemoteIcon />}
                              onClick={() =>
                                handleOpenConfirmDialog(user.id, 'demote', user.email)
                              }
                              disabled={user.id === currentUser?.id}
                            >
                              Rétrograder
                            </Button>
                          </Tooltip>
                        )}

                        {/* Bouton supprimer (sauf pour soi-même) */}
                        {user.id !== currentUser?.id && (
                          <Tooltip title="Supprimer définitivement">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() =>
                                handleOpenConfirmDialog(user.id, 'delete', user.email)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {user.account_status !== 'approved' && user.account_status === 'rejected' && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Compte rejeté - Vous pouvez le supprimer
                        </Typography>
                      )}
                      {user.account_status !== 'approved' && user.account_status === 'pending' && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Le compte doit être approuvé
                        </Typography>
                      )}
                      {user.id === currentUser?.id && user.role === 'admin' && (
                        <Typography
                          variant="caption"
                          color="info.main"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Impossible de se rétrograder soi-même
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Summary */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Total utilisateurs
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {users.length}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Administrateurs
          </Typography>
          <Typography variant="h4" fontWeight={600} color="secondary.main">
            {users.filter((u) => u.role === 'admin').length}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Utilisateurs
          </Typography>
          <Typography variant="h4" fontWeight={600} color="primary.main">
            {users.filter((u) => u.role === 'user').length}
          </Typography>
        </Paper>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog}>
        <DialogTitle>
          {confirmDialog.action === 'promote'
            ? 'Confirmer la promotion'
            : confirmDialog.action === 'demote'
            ? 'Confirmer la rétrogradation'
            : 'Confirmer la suppression'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'promote' ? (
              <>
                Êtes-vous sûr de vouloir <strong>promouvoir</strong>{' '}
                <strong>{confirmDialog.email}</strong> en tant qu'administrateur ?
                <br />
                <br />
                Cet utilisateur aura accès à toutes les fonctionnalités d'administration :
                <ul>
                  <li>Gestion des utilisateurs</li>
                  <li>Approbation des comptes</li>
                  <li>Vue de tous les QR codes</li>
                </ul>
              </>
            ) : confirmDialog.action === 'demote' ? (
              <>
                Êtes-vous sûr de vouloir <strong>rétrograder</strong>{' '}
                <strong>{confirmDialog.email}</strong> en tant qu'utilisateur ?
                <br />
                <br />
                Cet utilisateur perdra tous ses privilèges d'administration.
              </>
            ) : (
              <>
                Êtes-vous sûr de vouloir <strong>supprimer définitivement</strong> le compte de{' '}
                <strong>{confirmDialog.email}</strong> ?
                <br />
                <br />
                <strong style={{ color: '#d32f2f' }}>Cette action est irréversible !</strong>
                <br />
                Le compte et toutes ses données associées (QR codes, sessions, etc.) seront supprimés
                de la base de données.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={
              confirmDialog.action === 'promote'
                ? 'secondary'
                : confirmDialog.action === 'demote'
                ? 'primary'
                : 'error'
            }
            variant="contained"
            autoFocus
          >
            {confirmDialog.action === 'promote'
              ? 'Promouvoir'
              : confirmDialog.action === 'demote'
              ? 'Rétrograder'
              : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;
